/**
 * @file Règles de conversion du contenu collé dans l'éditeur riche (Quill 1).
 * @module common.utils.editorPasteMatchers
 *
 * Le collage passe par le presse-papiers natif de Quill 1 (celui de react-quill).
 * Ces règles corrigent deux pertes constatées en collant depuis Facebook,
 * WhatsApp Web, Gmail ou Messenger :
 * - les emojis y sont des images dont le texte alternatif est l'emoji : sans
 *   règle, l'image est retirée (format non autorisé) et l'emoji disparaît ;
 * - le gras y est écrit `*texte*` : les astérisques arrivent tels quels.
 */

import {Quill} from 'react-quill';

const Delta = Quill.import('delta');

/** Un texte alternatif d'emoji contient un pictogramme ou un drapeau. */
const EMOJI_PATTERN = /\p{Extended_Pictographic}|\p{Regional_Indicator}/u;

/**
 * Longueur maximale d'un texte alternatif traité comme emoji : au-delà, c'est
 * une description de photo (« Peut être une image de... »), jamais insérée.
 */
const MAX_EMOJI_ALT_LENGTH = 16;

/**
 * Gras façon réseaux sociaux : `*texte*` collé à des bords non blancs, hors
 * mot (un « 5 * 3 * 2 » ou un « prix*  » restent intacts).
 */
const SOCIAL_BOLD_PATTERN = /(^|[^\p{L}\p{N}*])\*(?=\S)([^*\n]*?\S)\*(?![\p{L}\p{N}*])/gu;

/**
 * Remplace une image d'emoji par l'emoji lui-même. Les autres images restent
 * telles quelles : l'éditeur les garde ou les retire selon ses formats.
 *
 * @param {HTMLElement} node - Élément IMG collé
 * @param {Object} delta - Delta produit par Quill pour cet élément
 * @return {Object} Delta à insérer
 */
export const matchEmojiImage = (node, delta) => {
  const alt = node.getAttribute('alt') || '';
  if (alt.length > MAX_EMOJI_ALT_LENGTH || !EMOJI_PATTERN.test(alt)) return delta;
  return new Delta().insert(alt);
};

/**
 * Découpe un texte en segments, `*texte*` devenant un segment gras sans ses astérisques.
 *
 * @param {string} text - Texte collé
 * @param {Object} [attributes] - Mise en forme déjà portée par le texte
 * @return {Array<Object>} Opérations Delta
 */
const splitSocialBold = (text, attributes) => {
  const ops = [];
  const push = (insert, extra) => {
    if (!insert) return;
    const merged = {...attributes, ...extra};
    ops.push(Object.keys(merged).length > 0 ? {insert, attributes: merged} : {insert});
  };

  let cursor = 0;
  for (const match of text.matchAll(SOCIAL_BOLD_PATTERN)) {
    const [whole, prefix, boldText] = match;
    push(text.slice(cursor, match.index) + prefix);
    push(boldText, {bold: true});
    cursor = match.index + whole.length;
  }
  push(text.slice(cursor));
  return ops;
};

/**
 * Convertit le gras façon Facebook/WhatsApp d'un contenu collé. Appliqué au
 * collage seulement (voir PasteAwareClipboard) : un texte enregistré n'est jamais
 * réécrit à l'ouverture de l'éditeur.
 *
 * @param {Object} delta - Delta du contenu collé
 * @return {Object} Delta avec les segments `*texte*` en gras
 */
export const applySocialBold = (delta) =>
  new Delta(delta.ops.flatMap((op) => (
    typeof op.insert === 'string' ? splitSocialBold(op.insert, op.attributes) : [op]
  )));
